/* eslint-disable jsx-a11y/anchor-is-valid */
import classNames from 'classnames';
import _ from 'lodash';
import { darken, rgba } from 'polished';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Icon } from '~/components';
import { useApi, useConfirmation, useMember, useRating, useSession, useSubscription, useWorkspace } from '~/contexts';
import { useActions, useAuth } from '~/hooks';
import { colors, weights } from '~/styles';
import { PUBLIC_BASE_URL } from '~/utils/redirectPublic';
import LogoutConfirmation from './LogoutConfirmation';
import moment from 'moment';

const Selector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  width: 4.0625rem;
  cursor: pointer;
  user-select: none;
  border: solid 0.1875rem ${({ adminImpersonating }) => (adminImpersonating ? colors.danger : 'transparent')};

  &:hover {
    border-color: ${({ adminImpersonating }) => (adminImpersonating ? colors.danger : colors.grey5)};
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 0.375rem);
  right: 0.375rem;
  width: 16.375rem;
  display: none;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 5.75rem);
  background-color: ${colors.white};
  border-radius: 0.625rem;
  box-shadow: 0 0.25rem 1.5rem ${rgba(colors.black, 0.15)};
`;

const MenuBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  overflow-y: auto;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-self: stretch;
  border-left: solid 1px ${colors.grey10};
  background-color: ${({ adminImpersonating }) => (adminImpersonating ? colors.danger10 : 'transparent')};

  &.is-open {
    ${Selector} {
      border-color: ${({ adminImpersonating }) => (adminImpersonating ? colors.danger : colors.grey5)};
    }

    ${Menu} {
      display: flex;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.125rem;
  height: 2.125rem;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: ${colors.danger};
  font-size: 0.875rem;
  font-weight: normal;
  cursor: pointer;

  &:hover {
    color: ${darken(0.1, colors.danger)};
    background: transparent;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  gap: 0.625rem;
  padding-top: 1.875rem;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  line-height: 1.15;
  text-align: center;
`;

const MemberName = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  color: ${colors.black};
`;

const WorkspaceName = styled.p`
  margin: 0;
  font-size: 0.625rem;
  font-weight: ${weights.medium};
  color: ${colors.grey40};
`;

const ProfileButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0.5625rem 0 1.875rem;
  padding: 0 1.5rem;
  width: 9.9375rem;
  height: 1.5rem;
  background-color: ${colors.primary};
  border-radius: 2.5rem;
  color: ${colors.white};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: ${colors.white};
    background-color: ${colors.accent};
  }
`;

const Divider = styled.div`
  align-self: stretch;
  flex-shrink: 0;
  height: 1px;
  background-color: ${colors.grey5};
`;

const MenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 0;
`;

const MenuLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  color: ${colors.black};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  .icon {
    font-size: 0.75rem;
    color: ${colors.grey25};
    width: 0.8125rem;
    text-align: center;
  }

  &:hover {
    color: ${colors.primary};

    .icon {
      color: ${colors.primary};
    }
  }
`;

const MenuLinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-weight: ${weights.normal};
  color: ${colors.black};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  .icon {
    font-size: 0.75rem;
    color: ${colors.grey25};
    width: 0.8125rem;
    text-align: center;
  }

  &:hover {
    color: ${colors.primary};

    .icon {
      color: ${colors.primary};
    }
  }
`;

const WorkspaceRow = styled.button`
  display: flex;
  align-items: center;
  align-self: stretch;
  flex-shrink: 0;
  height: 2.75rem;
  padding: 0 1.625rem;
  border: none;
  border-top: solid 1px ${colors.grey5};
  border-radius: 0;
  background: transparent;
  color: inherit;
  font-weight: normal;
  cursor: pointer;

  &:hover,
  &[aria-expanded='true'] {
    background-color: ${colors.grey5};
    color: inherit;
  }
`;

const WorkspaceRowInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 0.625rem;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const WorkspaceRowName = styled.span`
  font-size: 0.75rem;
  font-weight: ${weights.normal};
  color: ${colors.black};
  line-height: 1.15;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WorkspaceRowRole = styled.span`
  font-size: 0.625rem;
  font-weight: ${weights.medium};
  color: ${colors.grey40};
  line-height: 1.15;
`;

const WorkspaceLogo = styled.img`
  width: 1.5625rem;
  height: 1.5625rem;
  object-fit: contain;
  flex-shrink: 0;
`;

const WorkspaceChevron = styled.div`
  margin-left: auto;
  padding-left: 0.5rem;
  font-size: 0.5rem;
  color: ${colors.black};

  .icon {
    transform: ${({ $open }) => ($open ? 'scaleY(-1)' : 'none')};
  }
`;

const WorkspaceSwitcher = styled.div`
  position: absolute;
  top: ${({ $top }) => $top / 16}rem;
  right: calc(0.375rem + 16.375rem - 0.3125rem);
  width: 16.375rem;
  max-height: 13.75rem;
  overflow-y: auto;
  background-color: ${colors.white};
  border-radius: 0.625rem;
  box-shadow: 0 0.25rem 1.5rem ${rgba(colors.black, 0.15)};
`;

const WorkspaceSwitcherRow = styled(Link)`
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.75rem;
  padding: 0 1.625rem;
  text-decoration: none;

  &:not(:first-child) {
    border-top: solid 1px ${colors.grey5};
  }

  &:hover {
    background-color: ${colors.grey5};
  }
`;

const BottomAction = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
  padding: 0.9375rem 0.625rem;
  border-top: solid 1px ${colors.grey5};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${({ $color }) => $color};
  text-decoration: none;
  text-align: center;

  &:hover {
    color: ${({ $color }) => darken(0.1, $color)};
  }
`;

const LogOutAction = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 100%;
  padding: 0.9375rem 0.625rem;
  border-top: solid 1px ${colors.grey5};
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${colors.danger};
  text-decoration: none;
  text-align: center;

  &:hover {
    color: ${darken(0.1, colors.danger)};
  }
`;

const initialState = { workspaces: [], isReady: false };

const handlers = {
  ready: ({ workspaces }) => ({ workspaces, isReady: true }),
};

export default function WorkspaceMenu() {
  const api = useApi();
  const auth = useAuth();

  const { isAdmin, isImpersonation } = useSession();
  const { workspace } = useWorkspace();
  const { member } = useMember();
  const { subscribe, unsubscribe } = useSubscription();
  const { ratingState, openRatingModal, openReviewModal } = useRating();
  const [{ workspaces }, actions] = useActions(handlers, initialState);
  const [isOpen, setOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [switcherTop, setSwitcherTop] = useState(0);
  const selectorElement = useRef();
  const menuElement = useRef();
  const switcherElement = useRef();

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces().get();
      actions.ready({ workspaces: data });
    } catch (error) {
      actions.ready({ workspaces: [] });
    }
  }, [actions, api]);

  useEffect(() => {
    const sid = subscribe([useSubscription.keys.workspace_changed, useSubscription.keys.workspace_deleted], () => {
      fetchData();
    });
    fetchData();
    return () => {
      unsubscribe(sid);
    };
  }, [fetchData, subscribe, unsubscribe]);

  useEffect(() => {
    function closeMenu(event) {
      if (selectorElement.current && selectorElement.current.contains(event.target)) {
        return false;
      }
      if (menuElement.current && menuElement.current.contains(event.target)) {
        return false;
      }
      if (switcherElement.current && switcherElement.current.contains(event.target)) {
        return false;
      }
      setOpen(false);
      setSwitcherOpen(false);
    }

    if (isOpen) {
      document.addEventListener('mouseup', closeMenu);
    }

    return function cleanup() {
      document.removeEventListener('mouseup', closeMenu);
    };
  }, [isOpen]);

  const confirmation = useConfirmation();

  const handleLogoutClick = async (event) => {
    if (workspaces.length > 1) {
      event.preventDefault();
      await confirmation.prompt((resolve) => <LogoutConfirmation onResolve={resolve} />);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSwitcherOpen(false);
  };

  const handleMenuClick = (event) => {
    // Close workspace switcher when clicking anywhere in the menu (except the switcher toggle itself)
    const wsToggle = event.target.closest('[aria-expanded]');
    if (!wsToggle) {
      setSwitcherOpen(false);
    }

    // Close menu when clicking a link or button-like action
    const target = event.target.closest('a[href], [data-close-menu]');
    if (target) {
      handleClose();
    }
  };

  const hasMultipleWorkspaces = !isImpersonation && workspaces.length > 1;
  const currentWs = hasMultipleWorkspaces ? workspaces.find((ws) => ws.id === workspace?.id) : null;
  const otherWorkspaces = hasMultipleWorkspaces ? workspaces.filter((ws) => ws.id !== workspace?.id) : [];

  return (
    <Container
      adminImpersonating={isImpersonation}
      className={classNames({ 'is-open': isOpen })}
      data-testid="workspace_menu">
      <Selector ref={selectorElement} onClick={() => setOpen(!isOpen)} adminImpersonating={isImpersonation}>
        {member && <Avatar value={member} size={40} isCircle hasBackground />}
      </Selector>
      <Menu ref={menuElement} onClick={handleMenuClick}>
        <CloseButton onClick={handleClose}>
          <Icon icon="xmark" />
        </CloseButton>

        <AccountInfo>
          {member && <Avatar value={member} size={60} isCircle hasBackground />}
          <NameContainer>
            <MemberName>{member?.name}</MemberName>
            {!hasMultipleWorkspaces && <WorkspaceName>{workspace?.name}</WorkspaceName>}
          </NameContainer>
        </AccountInfo>

        <ProfileButton to={`/app/${workspace?.key}/my-profile`}>My Profile</ProfileButton>

        {hasMultipleWorkspaces && (
          <WorkspaceRow
            aria-expanded={switcherOpen}
            onClick={(e) => {
              if (!switcherOpen) {
                const containerRect = selectorElement.current?.parentElement?.getBoundingClientRect();
                const rowRect = e.currentTarget.getBoundingClientRect();
                if (containerRect) setSwitcherTop(rowRect.top - containerRect.top);
              }
              setSwitcherOpen(!switcherOpen);
            }}>
            {workspace?.imageUrl ? (
              <WorkspaceLogo src={workspace.imageUrl} alt={workspace.name} />
            ) : (
              <Avatar value={{ ...workspace, name: workspace?.name?.charAt(0) }} size={25} isCircle hasBackground />
            )}
            <WorkspaceRowInfo>
              <WorkspaceRowName>{workspace?.name}</WorkspaceRowName>
              {currentWs?.securityRoleName && <WorkspaceRowRole>{currentWs.securityRoleName}</WorkspaceRowRole>}
            </WorkspaceRowInfo>
            <WorkspaceChevron $open={switcherOpen}>
              <Icon icon="chevron-down" />
            </WorkspaceChevron>
          </WorkspaceRow>
        )}

        <Divider />

        <MenuBody>
          <MenuLinks>
            {isAdmin && (
              <MenuLinkStyled to="/admin">
                <Icon icon="wrench" />
                Admin
              </MenuLinkStyled>
            )}
            {auth.workspace.manage && moment().diff(moment(workspace.createdAt), 'days') < 90 && (
              <MenuLink
                href="https://help.ruddr.io/hc/en-us/articles/7891003800471-The-Ruddr-Guide-to-Setting-up-Your-Workspace"
                target="_blank"
                rel="noopener noreferrer">
                <Icon icon="rocket" />
                Setting Up Your Workspace
              </MenuLink>
            )}
            <MenuLink href={PUBLIC_BASE_URL} target="_blank" rel="noopener noreferrer">
              <Icon icon="arrow-up-right-from-square" />
              Go to Ruddr.com
            </MenuLink>
            {ratingState.isEligibleForRating && (
              <MenuLink data-close-menu onClick={openRatingModal}>
                <Icon type="far" icon="star" />
                How are we Doing?
              </MenuLink>
            )}
            {ratingState.isEligibleForReview && (
              <MenuLink data-close-menu onClick={openReviewModal}>
                <Icon type="far" icon="star" />
                Receive $25 for Reviewing Ruddr!
              </MenuLink>
            )}
          </MenuLinks>

          <BottomAction to="/login" $color={colors.primary}>
            Log in to Another Workspace
          </BottomAction>
        </MenuBody>

        {!isImpersonation && (
          <LogOutAction to="/logout" onClick={handleLogoutClick}>
            Log Out
          </LogOutAction>
        )}
      </Menu>
      {switcherOpen && hasMultipleWorkspaces && (
        <WorkspaceSwitcher ref={switcherElement} $top={switcherTop}>
          {_.map(otherWorkspaces, (ws) => (
            <WorkspaceSwitcherRow key={ws.id} to={`/app/${ws.key}`}>
              {ws.imageUrl ? (
                <WorkspaceLogo src={ws.imageUrl} alt={ws.name} />
              ) : (
                <Avatar value={{ ...ws, name: ws.name?.charAt(0) }} size={25} isCircle hasBackground />
              )}
              <WorkspaceRowInfo>
                <WorkspaceRowName>{ws.name}</WorkspaceRowName>
                {ws.securityRoleName && <WorkspaceRowRole>{ws.securityRoleName}</WorkspaceRowRole>}
              </WorkspaceRowInfo>
            </WorkspaceSwitcherRow>
          ))}
        </WorkspaceSwitcher>
      )}
    </Container>
  );
}
